import { useState, useEffect, useRef } from "react";
import { message } from "antd";
import { debounce } from "lodash";
import { v4 as uuidv4 } from "uuid";
import db from "../../../database";
import useCartStore from "../../../stores/useCartStore";
import useAuthStore from "../../../stores/useAuthStore";
import { syncSingleProductByValue } from "../../../services";
import { getApplicablePrice } from "../../../utils/getPriceList";

const ITEMS_PER_LOAD = 50;

/**
 * Apply price list override to a product if applicable.
 */
function applyPriceList(product) {
  const currentCart = useCartStore.getState().cart;
  const selectedPrice = getApplicablePrice({
    priceList: product.priceList,
    selectedUomId: product.uomData?.[0]?.csUomId,
    sPriceListId: currentCart?.customer?.sPriceListId,
    alternateUom: product?.alternateUom || [],
    primaryUomId: product?.uom || null,
  });
  if (selectedPrice) {
    return { ...product, sunitprice: selectedPrice.pricestd, slistprice: selectedPrice.pricestd };
  }
  return product;
}

/**
 * Query products from DB with filters applied at DB level.
 * Never loads all 35K products — uses indexed queries + limit.
 */
// Shared filter predicate — handles all isactive variants (boolean, integer, string)
function isActiveProduct(p) {
  return p.isactive == true && p.productSegment !== "BG";
}

async function queryProducts({ brands, categories, searchValue, priceRange, offset = 0, limit = ITEMS_PER_LOAD, bestSellersOnly = false }) {
  // Use search index for text search (fast — in-memory)
  if (searchValue) {
    const results = await db.products.search({
      searchTerm: searchValue,
      limit: 500, // cap search results
      categoryId: categories?.length === 1 ? categories[0] : undefined,
      brandId: brands?.length === 1 ? brands[0] : undefined,
    });
    let filtered = results.filter(isActiveProduct);
    if (brands?.length > 1) filtered = filtered.filter((p) => brands.includes(p.brandId));
    if (categories?.length > 1) filtered = filtered.filter((p) => categories.includes(p.mProductCategoryId));
    if (bestSellersOnly) filtered = filtered.filter((p) => p.isBestSeller);
    const [pMin, pMax] = priceRange || [null, null];
    if (pMin != null || pMax != null) {
      filtered = filtered.filter((p) => {
        const price = p.sunitprice || 0;
        return (pMin == null || price >= pMin) && (pMax == null || price <= pMax);
      });
    }
    return filtered.slice(offset, offset + limit).map(applyPriceList);
  }

  // No search — load all products via toArray(), filter entirely in JS.
  // Do NOT use .filter() on DB collection — in SQLite mode, .filter() serializes
  // the function via fn.toString() for IPC eval, losing closure references
  // (isActiveProduct, brands, priceRange, etc.). Also, SQL LIMIT applies before
  // JS filters, giving fewer results. toArray() + Array.filter avoids both issues
  // and works identically for Dexie (Web) and SQLite (Electron).
  const allProducts = await db.products.toArray();
  let results = allProducts.filter(isActiveProduct);
  if (categories?.length > 0) results = results.filter((p) => categories.includes(p.mProductCategoryId));
  if (bestSellersOnly) results = results.filter((p) => p.isBestSeller);
  if (brands?.length > 0) results = results.filter((p) => brands.includes(p.brandId));
  const [pMin, pMax] = priceRange || [null, null];
  if (pMin != null || pMax != null) {
    results = results.filter((p) => {
      const price = p.sunitprice || 0;
      return (pMin == null || price >= pMin) && (pMax == null || price <= pMax);
    });
  }

  return results.slice(offset, offset + limit).map(applyPriceList);
}

/**
 * Get total count of active products (for display, not loading all).
 */
async function getProductCount() {
  try {
    const all = await db.products.toArray();
    return all.filter(isActiveProduct).length;
  } catch {
    return 0;
  }
}

async function getCategoryCounts() {
  const counts = {};
  const all = await db.products.toArray();
  all.filter(isActiveProduct).forEach((p) => {
    const catId = p.mProductCategoryId || "uncategorized";
    counts[catId] = (counts[catId] || 0) + 1;
  });
  return counts;
}

const useProductPanelFilters = ({
  filterDrawer,
  setFilterDrawer,
  productSearchInput,
  setProductSearchInput,
  setProductsList,
  productsList,
  setLayoutName,
  setOpen,
  setInputName,
  keyboard,
  searchInputRef,
  productPanelSearchRef,
  pickProduct,
  setIsProductsVisible,
  debouncedSearch: externalDebouncedSearch,
}) => {
  const [brandsData, setBrandsData] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [selectedProductBrand, setSelectedProductBrand] = useState([]);
  const [selectCategoryList, setSelectCategoryList] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [originalProducts, setOriginalProducts] = useState(0);
  const [priceRange, setPriceRange] = useState([null, null]);
  const [allProductCategories, setAllProductCategories] = useState([]);
  const [displayedNonFrequentProducts, setDisplayedNonFrequentProducts] = useState([]);
  const [hasMoreNonFrequent, setHasMoreNonFrequent] = useState(true);
  const [sortOrder, setSortOrder] = useState(null);
  const debounceTimeout = useRef(null);
  const scrollContainerRef = useRef(null);
  const currentOffset = useRef(0);
  // Monotonic generation counter so late-returning async queries don't overwrite
  // newer results (e.g. initial full-load finishing AFTER a user-typed search).
  const queryGenRef = useRef(0);

  const filteredProducts = productsList.filter((item) => selectedCategory === "All" || item.category === selectedCategory);
  const frequentProducts = filteredProducts.filter((item) => item.isBestSeller);
  const nonfrequentProducts = filteredProducts.filter((item) => !item.isBestSeller);

  useEffect(() => {
    if (filterDrawer && !loading) {
      setTimeout(() => {
        if (productPanelSearchRef.current) {
          productPanelSearchRef.current.focus();
        }
      }, 300);
    }
  }, [filterDrawer, loading]);

  // Initial data load — only loads first page, NOT all 35K products
  useEffect(() => {
    setLoading(true);
    // Each time the drawer opens we start a fresh generation. filterProducts()
    // bumps the counter on every user action. If this initial fetch returns
    // after the user has already searched/filtered, we skip the setState so the
    // search results are not clobbered.
    queryGenRef.current += 1;
    const myGen = queryGenRef.current;
    const fetchData = async () => {
      // Load categories and brands (small tables — safe to load all)
      const [cats, brands, allCats] = await Promise.all([db.productCategories.toArray(), db.productBrands.toArray(), db.AllProductCategories.toArray()]);

      setCategoriesData(cats.map((c) => ({ ...c, label: c.value, value: c.mProductCategoryId })).sort((a, b) => a.label.localeCompare(b.label)));
      setBrandsData(brands.map((b) => ({ ...b, label: b.name, value: b.brandId })).sort((a, b) => a.label.localeCompare(b.label)));
      setAllProductCategories(allCats);

      // Load only first page of products (not all 35K)
      const [bestSellers, firstPage, totalCount, counts] = await Promise.all([
        queryProducts({ bestSellersOnly: true, limit: 200 }),
        queryProducts({ offset: 0, limit: ITEMS_PER_LOAD }),
        getProductCount(),
        getCategoryCounts(),
      ]);

      // If a newer query started (e.g. user typed before we finished), drop results.
      if (myGen !== queryGenRef.current) return;

      const allLoaded = [...bestSellers, ...firstPage.filter((p) => !p.isBestSeller)];
      setProductsList(allLoaded);
      setDisplayedNonFrequentProducts(firstPage.filter((p) => !p.isBestSeller));
      setHasMoreNonFrequent(totalCount > ITEMS_PER_LOAD + bestSellers.length);
      setOriginalProducts(totalCount);
      setCategoryCounts(counts);
      currentOffset.current = ITEMS_PER_LOAD;
      setLoading(false);
    };
    filterDrawer ? fetchData() : onFilterClose();
  }, [filterDrawer]);

  // Filter products — uses DB-level queries, not loading all
  const filterProducts = async (brands, categories, searchValue = "", priceRangeFilter = [null, null]) => {
    // Bump generation so any in-flight initial-load won't overwrite our results
    queryGenRef.current += 1;
    const myGen = queryGenRef.current;
    try {
      const noFilters = !searchValue && brands.length === 0 && categories.length === 0 && !priceRangeFilter[0] && !priceRangeFilter[1];
      if (noFilters) {
        const [bestSellers, firstPage] = await Promise.all([queryProducts({ bestSellersOnly: true, limit: 200 }), queryProducts({ offset: 0, limit: ITEMS_PER_LOAD })]);
        if (myGen !== queryGenRef.current) return;
        const allLoaded = [...bestSellers, ...firstPage.filter((p) => !p.isBestSeller)];
        setProductsList(allLoaded);
        setDisplayedNonFrequentProducts(firstPage.filter((p) => !p.isBestSeller));
        setHasMoreNonFrequent(true);
        currentOffset.current = ITEMS_PER_LOAD;
        return;
      }
      let results = await queryProducts({
        brands,
        categories,
        searchValue,
        priceRange: priceRangeFilter,
        offset: 0,
        limit: ITEMS_PER_LOAD * 4, // Load more for filtered results
      });

      // Cache miss on a real user-typed search → try on-demand single-product
      // sync once, then re-run the query. Ported from roadmap ad6b7339.
      // We only sync when there's a non-empty searchValue (never on initial
      // empty load or brand/category-only filtering — that would round-trip
      // the server for every empty focus).
      if (results.length === 0 && searchValue && String(searchValue).trim().length >= 2) {
        const tillData = useAuthStore.getState().tillData;
        const tillId = tillData?.tillAccess?.cwrTill?.cwrTillID;
        if (tillData?.tillAccess?.csBunit?.csBunitId) {
          try {
            const synced = await syncSingleProductByValue(tillData, uuidv4(), tillId, String(searchValue).trim(), { ignoreLastSync: true });
            // Still stale? Drop.
            if (myGen !== queryGenRef.current) return;
            if (synced) {
              results = await queryProducts({
                brands,
                categories,
                searchValue,
                priceRange: priceRangeFilter,
                offset: 0,
                limit: ITEMS_PER_LOAD * 4,
              });
            }
          } catch (err) {
            console.warn("[ProductPanel] single-product sync failed:", err?.message);
          }
        }
      }

      // Drop stale results if the user triggered another query in the meantime.
      if (myGen !== queryGenRef.current) return;
      setProductsList(results);
      setDisplayedNonFrequentProducts(results.filter((item) => !item.isBestSeller).slice(0, ITEMS_PER_LOAD));
      setHasMoreNonFrequent(results.filter((item) => !item.isBestSeller).length > ITEMS_PER_LOAD);
      currentOffset.current = ITEMS_PER_LOAD;
    } catch (error) {
      console.error("Error filtering products:", error);
    }
  };

  // Keep a stable debounced handler across renders so timers are cancelled
  // correctly when the user types quickly. Without useRef, every render created
  // a fresh debounce whose previous timer kept firing — duplicate calls and
  // stale closures over selectedProductBrand/selectCategoryList/priceRange.
  const filterStateRef = useRef({ brands: [], categories: [], priceRange: [null, null] });
  useEffect(() => {
    filterStateRef.current = { brands: selectedProductBrand, categories: selectCategoryList, priceRange };
  }, [selectedProductBrand, selectCategoryList, priceRange]);

  const debouncedSearch = useRef(
    debounce((searchValue) => {
      const { brands, categories, priceRange: pr } = filterStateRef.current;
      filterProducts(brands, categories, searchValue, pr);
    }, 300)
  ).current;

  useEffect(() => () => debouncedSearch.cancel(), []);

  const debouncedBrandChange = debounce((brandId) => {
    const isSelected = selectedProductBrand?.includes(brandId);
    let updatedSelectedBrands = [];
    if (isSelected && selectedProductBrand?.length > 0) {
      updatedSelectedBrands = selectedProductBrand?.filter((id) => id !== brandId);
    } else {
      let tempBrand = selectedProductBrand || [];
      updatedSelectedBrands = [...tempBrand, brandId];
    }
    setSelectedProductBrand(updatedSelectedBrands || []);
    filterProducts(updatedSelectedBrands || [], selectCategoryList, productSearchInput, priceRange);
  }, 300);

  const handleBrandCheckboxChange = (brandId) => {
    debouncedBrandChange(brandId);
  };

  const handleCategoryCheckboxChange = (categoryId) => {
    const isSelected = selectCategoryList?.includes(categoryId);
    let updatedSelectedCategories = [];
    if (isSelected && selectCategoryList?.length > 0) {
      updatedSelectedCategories = selectCategoryList?.filter((id) => id !== categoryId);
    } else {
      let tempCat = selectCategoryList || [];
      updatedSelectedCategories = [...tempCat, categoryId];
    }
    setSelectCategoryList(updatedSelectedCategories || []);
    filterProducts(selectedProductBrand, updatedSelectedCategories || [], productSearchInput, priceRange);
  };

  const handleSortChange = (value) => {
    setSortOrder(value);
    // Option values from the UI use underscores (price_asc, price_desc, name_asc).
    // Accept both underscore and hyphen variants so legacy callers still work.
    const norm = (v) => String(v || "").replace(/-/g, "_");
    const v = norm(value);
    const sorted = [...productsList].sort((a, b) => {
      if (v === "name_asc") return (a.name || "").localeCompare(b.name || "");
      if (v === "name_desc") return (b.name || "").localeCompare(a.name || "");
      if (v === "price_asc") return (a.sunitprice || 0) - (b.sunitprice || 0);
      if (v === "price_desc") return (b.sunitprice || 0) - (a.sunitprice || 0);
      return 0;
    });
    setProductsList(sorted);
    setDisplayedNonFrequentProducts(sorted.filter((item) => !item.isBestSeller).slice(0, ITEMS_PER_LOAD));
  };

  const handleSearchEnter = async (e) => {
    if (e.key === "Enter" && productSearchInput) {
      const searchTerm = productSearchInput.toLowerCase();
      const match = productsList.find((p) => p.value?.toLowerCase() === searchTerm || p.upc?.toLowerCase() === searchTerm);
      if (match) {
        pickProduct?.(match, 1);
        setProductSearchInput("");
      } else {
        const allProducts = await db.products.toArray();
        const dbMatch = allProducts.find((p) => p.isactive == true && (p.value?.toLowerCase() === searchTerm || p.upc?.toLowerCase() === searchTerm));
        if (dbMatch) {
          pickProduct?.(dbMatch, 1);
          setProductSearchInput("");
        }
      }
    }
  };

  const onFilterClose = () => {
    setSelectedProductBrand([]);
    setSelectCategoryList([]);
    setPriceRange([null, null]);
    setSortOrder(null);
    setProductSearchInput("");
    if (keyboard?.current) keyboard.current.setInput("");
  };

  // Close the multi-product selection drawer (triggered by X button)
  const onProductsClose = () => {
    setIsProductsVisible?.(false);
    setProductSearchInput("");
    if (keyboard?.current) keyboard.current.setInput("");
  };

  // Load more products on scroll (paginated)
  const handleScrollLoad = async () => {
    if (!hasMoreNonFrequent) return;
    const nextBatch = await queryProducts({
      brands: selectedProductBrand,
      categories: selectCategoryList,
      searchValue: productSearchInput,
      priceRange,
      offset: currentOffset.current,
      limit: ITEMS_PER_LOAD,
    });
    if (nextBatch.length === 0) {
      setHasMoreNonFrequent(false);
      return;
    }
    const nonFrequent = nextBatch.filter((p) => !p.isBestSeller);
    setDisplayedNonFrequentProducts((prev) => [...prev, ...nonFrequent]);
    setProductsList((prev) => [...prev, ...nextBatch]);
    currentOffset.current += ITEMS_PER_LOAD;
    if (nonFrequent.length < ITEMS_PER_LOAD) setHasMoreNonFrequent(false);
  };

  return {
    brandsData,
    categoriesData,
    selectedProductBrand,
    setSelectedProductBrand,
    selectCategoryList,
    setSelectCategoryList,
    categoryCounts,
    loading,
    selectedCategory,
    setSelectedCategory,
    originalProducts,
    priceRange,
    setPriceRange,
    allProductCategories,
    displayedNonFrequentProducts,
    hasMoreNonFrequent,
    sortOrder,
    setSortOrder,
    frequentProducts,
    nonfrequentProducts,
    filterProducts,
    debouncedSearch,
    handleBrandCheckboxChange,
    handleCategoryCheckboxChange,
    handleSortChange,
    handleSearchEnter,
    onFilterClose,
    onProductsClose,
    handleScrollLoad,
    scrollContainerRef,
  };
};

export default useProductPanelFilters;
